/**
 * @module app.core
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting, IForm } from 'app/shared/lib/essette-site';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/api/api';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { MemberService } from 'app/cd/services/member-service';
import { IPopup } from 'app/shared/elements/popup-base';
import { SaveAidCode } from 'app/core/components/member/save-aid-code';
import { validate } from 'app/shared/elements/ess-rule';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import * as CD from 'app/cd/models/essette-api';

@autoinject
export class memberCodesAidCodes extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView })
    public memberPlanId: string | null = null;

    @bindable
    protected memberPlanIsActive: boolean = false;

    @bindable
    protected includeInactive: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected aidCodes: Array<CD.AidCode> = [];

    protected memberAidCodes: Array<CD.MemberAidCode> = [];
    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 10;

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $saveAidCodeModal!: IPopup;
    protected $form!: IForm;
    protected $aidCodeForm!: Element;
    protected $saveAidCode!: SaveAidCode;
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.memberPlanId) { this.logger.warn('Bindable property [member-plan-id] was not set at binding'); }

        this.reload();
    }

    ////****************** Events ***************************************************////
    protected formSuccess({ detail }: FormSuccessCustomEvent) {
        this.$saveAidCodeModal.close();

        if(detail.form.reset) detail.form.reset();

        // reload grid and reset page
        this.reload();
    }

    ////****************** Functions ************************************************////
    protected saveAidCode(memberAidCode: CD.MemberAidCode): void {
        this.$saveAidCode.memberAidCode = memberAidCode;
        this.$saveAidCode.aidCode = memberAidCode?.aidCode;
        this.$saveAidCode.aidCodeStart = memberAidCode?.dateRange?.start;
        this.$saveAidCode.aidCodeEnd = memberAidCode?.dateRange?.end;
        this.$saveAidCodeModal.open();
    }

    protected saveAidCodeClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$saveAidCodeModal.close();
    }

    public async reload(): Promise<void> {
        await this.loadMemberAidCodesByPlan();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberAidCodesByPlan(): Promise<void> {
        if(!this.memberPlanId) return;

        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        const includes: string[] = [
            'aidCode'
        ];

        const fields: string[] = [
            'aidCode.description',
            'daterange.start',
            'daterange.end'
        ];

        try {
            this.state.beginLoad();

            const response = await this.memberService.loadMemberAidCodesByPlan(this.memberPlanId, includes, undefined, options, fields);
            this.memberAidCodes = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateMemberAidCode(memberAidCode: CD.MemberAidCode): Promise<void> {
        if(!memberAidCode.id) return;

        // Show confirmation dialog only if deactivating
        if(memberAidCode.isActive) {
            const result = await confirmation.showDeactivate('cd:aidCode.$', 1, false);
            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.memberService.activateMemberAidCode(memberAidCode.id, !memberAidCode.isActive);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            this.toaster.standard.deleted('cd:aidCode.$', false);
            throw error;
        }
        finally {
            this.toaster.standard.activate('cd:aidCode.$', !memberAidCode.isActive, isSuccessful);
            this.state.endSave();
            await this.reload();
        }
    }
}
